// Hero section — combined cross-lens regional summary banner.
//
// In the unified-lens world there's ONE hero summary that covers both
// whitewater + fly fishing together. The model is told to weave in
// counts across both lenses (e.g. "32 fishing well, 27 running well").

import type { RegionalSummary } from '../../functions/_lib/types';
import { formatTimeAgo } from '../lib/format';

export function renderHero(summary: RegionalSummary | null, generatedAt: string | null): string {
  // Cross-lens gradient — warm cream blending into steel blue. Brand-of-a-piece.
  const gradient = 'from-[oklch(28%_0.05_220/0.7)] via-[oklch(24%_0.04_60/0.5)] to-[oklch(20%_0.03_250/0.4)]';

  if (!summary) {
    return `
      <section class="relative overflow-hidden rounded-2xl bg-gradient-to-br ${gradient} backdrop-blur p-6 md:p-8 border border-primary/10">
        <img src="/frozen-fly-badge.png" alt="" class="brand-badge-watermark" aria-hidden="true" />
        <div class="relative">
          <div class="tagline-ribbon mb-3">The Week's Read</div>
          <div class="skeleton h-9 w-72 mb-3 bg-base-300/40"></div>
          <div class="space-y-2">
            <div class="skeleton h-3 w-full max-w-2xl bg-base-300/40"></div>
            <div class="skeleton h-3 w-5/6 max-w-2xl bg-base-300/40"></div>
            <div class="skeleton h-3 w-3/4 max-w-2xl bg-base-300/40"></div>
          </div>
        </div>
      </section>
    `;
  }

  const pctOfNormal = summary.stats.median_percent_of_normal;
  const headlineLine = summary.headline;
  const narrative = summary.narrative;
  const eyebrow = summary.category === 'fly_fishing'
    ? 'The Week’s Read'
    : summary.category === 'whitewater'
      ? 'The Week’s Read'
      : 'The Week’s Read';

  return `
    <section class="relative overflow-hidden rounded-2xl bg-gradient-to-br ${gradient} backdrop-blur p-6 md:p-8 border border-primary/10">
      <img src="/frozen-fly-badge.png" alt="" class="brand-badge-watermark" aria-hidden="true" />
      <div class="relative">
        <div class="tagline-ribbon mb-3">${eyebrow}</div>
        <div class="flex flex-wrap items-baseline gap-x-6 gap-y-2 mb-3">
          <h1 class="font-display text-2xl md:text-3xl text-primary tracking-wide">${escapeHtml(headlineLine)}</h1>
          ${
            pctOfNormal !== null
              ? `<span class="text-sm text-base-content/70">regional median <span class="text-primary font-semibold">${pctOfNormal}%</span> of normal</span>`
              : ''
          }
        </div>
        ${
          narrative
            ? `<p class="text-base text-base-content/85 leading-relaxed max-w-3xl">${escapeHtml(narrative)}</p>`
            : `<p class="text-sm text-base-content/50 italic">Summary unavailable.</p>`
        }
        <div class="text-xs text-base-content/50 mt-4">
          Updated ${formatTimeAgo(generatedAt)}
        </div>
      </div>
    </section>
  `;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
